import { Box } from '@mui/material';
import type { PlatformIcons as PlatformIconMap } from '@dvgup/contracts/branding-config';
import { brandingFixture } from '../lib/branding/branding-fixture';

/**
 * Resolves an arbitrary upstream platform string to one of the seven
 * `PlatformIcons` keys, falling back to `'unknown'` per R20.9 / TODO-016.
 *
 * The input is lowercased and stripped of non-alphanumerics before
 * matching against substring patterns. The order of checks is
 * intentional — `steamdeck` must precede `steam` / `pc`, and
 * `nintendoswitch` must precede `nintendo`.
 */
export function resolvePlatformKey(platform: string): keyof PlatformIconMap {
  const normalized = platform.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (normalized.includes('playstation') || /^ps[0-9]?$/.test(normalized)) return 'playstation';
  if (normalized.includes('xbox')) return 'xbox';
  if (normalized.includes('switch') || normalized.includes('nintendo')) return 'nintendoSwitch';
  if (normalized.includes('steamdeck')) return 'steamDeck';
  if (normalized.includes('pc') || normalized.includes('steam') || normalized.includes('windows')) return 'pc';
  if (normalized.includes('mobile') || normalized.includes('ios') || normalized.includes('android')) return 'mobile';
  return 'unknown';
}

interface PlatformIconsProps {
  /** Upstream platform strings (e.g., `["PS5", "Xbox Series X"]`). */
  readonly platforms: ReadonlyArray<string>;
  /** Override icon map; defaults to the fixture's `platformIcons`. */
  readonly iconMap?: PlatformIconMap;
  /**
   * Per-icon size in pixels (square). Defaults to 24 to match the
   * surrounding body-text weight; the Daily-page release cards bump
   * this to ~32 so the icons scale alongside the action buttons.
   */
  readonly iconSize?: number;
}

/**
 * `<PlatformIcons>` — renders one icon per supplied platform string per
 * R20.9 / TODO-016. Each platform is resolved against
 * `BrandingConfig.platformIcons` via {@link resolvePlatformKey}; unknown
 * platforms render the `unknown` fallback so no record is silently
 * dropped (golden snapshot test per platform lives in task 5.7).
 *
 * _Requirements: R20.9, TODO-016, design §3.2._
 */
export function PlatformIcons({
  platforms,
  iconMap = brandingFixture.platformIcons,
  iconSize = 24,
}: PlatformIconsProps): JSX.Element {
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }} role="list" aria-label="platforms">
      {platforms.map((platform, index) => {
        const key = resolvePlatformKey(platform);
        return (
          <Box
            key={`${platform}-${index}`}
            role="listitem"
            component="img"
            alt={platform}
            src={iconMap[key]}
            sx={{ width: iconSize, height: iconSize }}
          />
        );
      })}
    </Box>
  );
}
